package app.nestbridge.feature.profile.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSurface
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.profile.screen.ProfileEvent
import app.nestbridge.feature.profile.screen.ProfileScreen
import app.nestbridge.feature.profile.screen.ProfileState
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = ProfileScreen::class,
    scope = AppScope::class,
)
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProfileUi(
    state: ProfileState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Profile",
        onBack = { state.eventSink(ProfileEvent.BackClicked) },
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.lg),
        ) {
            CoreSurface(
                modifier = Modifier.fillMaxWidth(),
            ) {
                Column(
                    modifier = Modifier.padding(CoreSpacing.lg),
                    verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
                ) {
                    Text(
                        text = state.displayName,
                        style = CoreTypography.headline,
                        color = CoreTheme.colors.textPrimary,
                    )
                }
            }

            Spacer(modifier = Modifier.height(4.dp))

            CorePrimaryButton(
                text = "Logout",
                onClick = {
                    state.eventSink(ProfileEvent.LogoutClicked)
                },
                modifier = Modifier.fillMaxWidth(),
            )

            if (state.passkeySupported) {
                CorePrimaryButton(
                    text = "Set up a passkey",
                    onClick = { state.eventSink(ProfileEvent.SetupPasskey) },
                    modifier = Modifier.fillMaxWidth(),
                )
                state.passkeyStatus?.let {
                    Text(
                        text = it,
                        style = CoreTypography.caption,
                        color = CoreTheme.colors.textSecondary,
                    )
                }
            }

            Spacer(modifier = Modifier.height(40.dp))

            Text(
                text = "Developer Tools:",
                style = CoreTypography.title,
                color = CoreTheme.colors.textMuted,
            )

            CorePrimaryButton(
                text = "Ping (No Auth)",
                onClick = {
                    state.eventSink(ProfileEvent.PingNoAuth)
                },
                modifier = Modifier.fillMaxWidth(),
            )

            CorePrimaryButton(
                text = "Ping (JWT)",
                onClick = {
                    state.eventSink(ProfileEvent.PingJwt)
                },
                modifier = Modifier.fillMaxWidth(),
            )

            CorePrimaryButton(
                text = "Ping (Session Token)",
                onClick = {
                    state.eventSink(ProfileEvent.PingSessionToken)
                },
                modifier = Modifier.fillMaxWidth(),
            )

            CorePrimaryButton(
                text = "Show Design Catalog",
                onClick = {
                    state.eventSink(ProfileEvent.ShowDesignCatalog)
                },
                modifier = Modifier.fillMaxWidth(),
            )

            state.pingResult?.let {
                Text("Result:\n$it")
            }
        }
    }
}

// region Previews

@Preview
@Composable
private fun ProfilePreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ProfileUi(
            state = ProfileState(
                displayName = "Karawek User",
                pingResult = "",
                eventSink = {},
            ),
        )
    }
}

// endregion
